import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import type { InstanceChangeDays } from "@keydials/api-client";
import {
  buildTimeline,
  changesThatDay,
  clampDay,
  countOn,
  createdLabel,
  dayAtFraction,
  dayAtPointer,
  daysBetween,
  fmtScrubDay,
  fractionOf,
  stepChangeDay,
  stepWeek,
  tickHeight,
} from "./scrubberModel";

/**
 * Then's value on the timeline. Only `then` is used today; `thenB` is the room left for comparing two past
 * days with each other, so the component's contract doesn't have to change when that arrives.
 */
export interface ScrubberValue {
  then: string;
  thenB?: string;
}

/** The track is drawn in a 1000-wide viewBox stretched to the row; only x scales, so the ticks stay upright. */
const VIEW_W = 1000;
const TRACK_Y = 24;

/**
 * Look back's timeline (KEY-LOOKBACK PR 4): the dial's life from creation to today, one tick per day
 * something changed, taller for busier days. Drag the handle and the date field follows at once; Then is
 * replayed when the pointer rests (the debounce lives in {@link useCompareMode}'s `previewDay`).
 *
 * Pointer input is the track's own; the keyboard and screen readers get a visually hidden
 * `<input type="range">`, whose arrow keys step between CHANGE days — the days worth looking at — rather than
 * single days, with Shift for a week and Home/End for the ends.
 */
export function Scrubber({ value, changeDays, onPreview, loading = false }: {
  value: ScrubberValue;
  changeDays: InstanceChangeDays;
  onPreview: (day: string) => void;
  /** A replay is in flight: a thin bar under the track says so without taking the scrubber away. */
  loading?: boolean;
}) {
  const timeline = useMemo(() => buildTimeline(changeDays), [changeDays]);
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [focused, setFocused] = useState(false);
  const day = clampDay(timeline, value.then);
  const frac = fractionOf(timeline, day);
  const count = countOn(timeline, day);

  const move = (day2: string) => {
    if (day2 !== value.then) onPreview(day2);
  };

  const fromPointer = (e: PointerEvent<HTMLDivElement>) => {
    const box = trackRef.current?.getBoundingClientRect();
    if (!box) return;
    move(dayAtPointer(timeline, e.clientX - box.left, box.width));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    let next: string | null = null;
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = e.shiftKey ? stepWeek(timeline, day, -1) : stepChangeDay(timeline, day, -1);
    else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = e.shiftKey ? stepWeek(timeline, day, 1) : stepChangeDay(timeline, day, 1);
    else if (e.key === "Home") next = timeline.start;
    else if (e.key === "End") next = timeline.end;
    if (next === null) return;
    e.preventDefault();
    move(next);
  };

  return (
    <div className="kd-scrubber" data-dragging={dragging ? "true" : undefined} data-loading={loading ? "true" : undefined}>
      <span className="kd-scrubber-end">{createdLabel(timeline)}</span>
      <div
        ref={trackRef}
        className="kd-scrubber-track"
        data-focused={focused ? "true" : undefined}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.currentTarget.setPointerCapture?.(e.pointerId);
          setDragging(true);
          fromPointer(e);
        }}
        onPointerMove={(e) => {
          if (dragging) fromPointer(e);
        }}
        onPointerUp={(e) => {
          e.currentTarget.releasePointerCapture?.(e.pointerId);
          setDragging(false);
        }}
        onPointerCancel={() => setDragging(false)}
      >
        <svg className="kd-scrubber-svg" viewBox={`0 0 ${VIEW_W} 36`} preserveAspectRatio="none" aria-hidden="true">
          <line className="kd-scrubber-rail" x1={0} x2={VIEW_W} y1={TRACK_Y} y2={TRACK_Y} vectorEffect="non-scaling-stroke" />
          <line className="kd-scrubber-span" x1={frac * VIEW_W} x2={VIEW_W} y1={TRACK_Y} y2={TRACK_Y} vectorEffect="non-scaling-stroke" />
          {timeline.ticks.map((t) => {
            const x = fractionOf(timeline, t.day) * VIEW_W;
            return (
              <line
                key={t.day}
                className="kd-scrubber-tick"
                data-day={t.day}
                data-on={t.day === day ? "true" : undefined}
                x1={x}
                x2={x}
                y1={TRACK_Y - 2}
                y2={TRACK_Y - 2 - tickHeight(timeline, t.count)}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        </svg>
        <span className="kd-scrubber-handle" style={{ left: `${frac * 100}%` }} aria-hidden="true" />
        {dragging && (
          <span className="kd-scrubber-tip" style={{ left: `${frac * 100}%` }} aria-hidden="true">
            {fmtScrubDay(day)} · {changesThatDay(count)}
          </span>
        )}
        {loading && <span className="kd-scrubber-progress" aria-hidden="true" />}
        <input
          type="range"
          className="kd-scrubber-input"
          aria-label="Then, along the dial's history"
          min={0}
          max={timeline.span}
          step={1}
          value={daysBetween(timeline.start, day)}
          aria-valuetext={`${fmtScrubDay(day)}, ${changesThatDay(count).toLowerCase()}`}
          onKeyDown={onKeyDown}
          // Anything that moves the slider without a key we handle (a screen reader's own increment).
          onChange={(e) => move(dayAtFraction(timeline, Number(e.target.value) / timeline.span))}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      </div>
      <span className="kd-scrubber-end">Today</span>
    </div>
  );
}
